import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, X } from 'lucide-react';
import { LoanResponse } from '@/types/api.types';
import { LoanTableRow } from './LoanTableRow';

interface LoansTableProps {
  loans: LoanResponse[];
  isLoading: boolean;
  error: Error | null;
  hasActiveFilters: boolean;
  onCreateNew: () => void;
  onClearFilters: () => void;
  onView: (loanId: number) => void;
  onEdit: (loanId: number) => void;
  onDelete: (loan: LoanResponse) => void;
  onSelect: (loan: LoanResponse) => void;
}

export const LoansTable = ({
  loans,
  isLoading,
  error,
  hasActiveFilters,
  onCreateNew,
  onClearFilters,
  onView,
  onEdit,
  onDelete,
  onSelect,
}: LoansTableProps) => {
  return (
    <Card className="p-0" data-test-id="loans-table-card">
      <div className="overflow-x-auto border border-gray-300 rounded-lg">
        <table
          className="relative table-responsive min-w-full divide-y divide-gray-200 overflow-hidden"
          data-test-id="loans-table"
        >
          <thead
            className="bg-gray-50 sticky top-0 z-10"
            data-test-id="loans-table-header"
          >
            <tr data-test-id="loans-table-header-row">
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                data-test-id="header-loan-id"
              >
                Loan ID
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                data-test-id="header-client-loan-number"
              >
                Client Loan Number
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                data-test-id="header-loan-name"
              >
                Loan Name
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                data-test-id="header-borrower"
              >
                Borrower
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                data-test-id="header-sector"
              >
                Sector
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                data-test-id="header-status"
              >
                Status
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                data-test-id="header-balance"
              >
                Current Balance
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                data-test-id="header-spread"
              >
                Credit Spread
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300"
                data-test-id="header-maturity"
              >
                Maturity
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                data-test-id="header-actions"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody
            className="bg-white divide-y divide-gray-200"
            data-test-id="loans-table-body"
          >
            {isLoading ? (
              <tr data-test-id="loading-row">
                <td
                  colSpan={10}
                  className="table-cell text-center py-8"
                  data-test-id="loading-cell"
                >
                  <div
                    className="flex items-center justify-center"
                    data-test-id="loading-content"
                  >
                    <Loader2
                      className="h-6 w-6 animate-spin mr-2"
                      data-test-id="loading-spinner"
                    />
                    Loading loans...
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr data-test-id="error-row">
                <td
                  colSpan={10}
                  className="table-cell text-center py-8 text-red-500"
                  data-test-id="error-cell"
                >
                  <div
                    className="flex flex-col items-center"
                    data-test-id="error-content"
                  >
                    <p>Failed to load loans. Please use the Refresh button in the header to retry.</p>
                  </div>
                </td>
              </tr>
            ) : loans.length === 0 ? (
              <tr data-test-id="empty-row">
                <td
                  colSpan={10}
                  className="table-cell text-center py-8 text-gray-500"
                  data-test-id="empty-cell"
                >
                  <div
                    className="flex flex-col items-center"
                    data-test-id="empty-content"
                  >
                    {hasActiveFilters ? (
                      <>
                        <p className="mb-2" data-test-id="no-matches-text">
                          No loans match your filters
                        </p>
                        <Button
                          variant="outline"
                          onClick={onClearFilters}
                          className="flex items-center gap-2"
                          data-test-id="clear-filters-empty-button"
                        >
                          <X
                            className="h-4 w-4"
                            data-test-id="clear-filters-empty-icon"
                          />
                          Clear Filters
                        </Button>
                      </>
                    ) : (
                      <>
                        <p className="mb-2" data-test-id="no-loans-text">
                          No loans found
                        </p>
                        <Button
                          onClick={onCreateNew}
                          className="flex items-center gap-2"
                          data-test-id="create-first-loan-button"
                        >
                          <Plus
                            className="h-4 w-4"
                            data-test-id="create-first-loan-icon"
                          />
                          Create Your First Loan
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              loans.map((loan, index) => (
                <LoanTableRow
                  key={loan.id}
                  loan={loan}
                  index={index}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onSelect={onSelect}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};
